import { useQueryClient, useMutation } from 'react-query';
import { useRequestQuery } from '@/hooks/useQueryRequest';
import { transformPagination } from '@/utils';
import useApiRequest from '@/hooks/useRequest';
import t from './type';

// Common invalidate queries.
const commonInvalidateQueries = (queryClient: any) => {
  // Invalidate projects.
  queryClient.invalidateQueries(t.PROJECT);
  queryClient.invalidateQueries(t.PROJECTS);
};

/**
 * Create a new project
 * @param props
 */
export function useCreateProject(props?: any) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values: any) => apiRequest.post('projects', values), {
    onSuccess: (res: any, values: any) => {
      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Edit the given project
 * @param props
 * @returns
 */
export function useEditProject(props?: any) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]: [any, any]) => apiRequest.put(`/projects/${id}`, values),
    {
      onSuccess: (res: any, [id, values]: [any, any]) => {
        // Invalidate specific project.
        queryClient.invalidateQueries([t.PROJECT, id]);

        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Delete the given project
 * @param props
 */
export function useDeleteProject(props?: any) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id: any) => apiRequest.delete(`projects/${id}`), {
    onSuccess: (res: any, id: any) => {
      // Invalidate specific project.
      queryClient.invalidateQueries([t.PROJECT, id]);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Retrieve the projects details.
 * @param projectId The project id
 * @param props
 * @param requestProps
 * @returns
 */
export function useProject(projectId: any, props?: any, requestProps?: any) {
  return useRequestQuery(
    [t.PROJECT, projectId],
    { method: 'get', url: `projects/${projectId}`, ...requestProps },
    {
      select: (res: any) => res.data.project,
      defaultData: {},
      ...props,
    },
  );
}

const transformProjects = (res: any) => ({
  projects: res.data.projects,
});

/**
 * Retrieve projects list with pagination meta.
 * @param query
 * @param props
 */
export function useProjects(query?: any, props?: any) {
  return useRequestQuery(
    [t.PROJECTS, query],
    { method: 'get', url: 'projects', params: query },
    {
      select: transformProjects,
      defaultData: {
        projects: [],
      },
      ...props,
    },
  );
}

/**
 *
 * @param props
 * @returns
 */
export function useProjectStatus(props?: any) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]: [any, any]) =>
      apiRequest.patch(`projects/${id}/status`, values),
    {
      onSuccess: (res: any, [id, values]: [any, any]) => {
        // Invalidate specific project.
        queryClient.invalidateQueries([t.PROJECT, id]);

        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/** Achats (bills) liés au projet. */
export function useProjectPurchasesList(projectId: any, props?: any) {
  return useRequestQuery(
    ['PROJECT_PURCHASES', projectId],
    {
      method: 'get',
      url: `projects/${projectId}/transactions/purchases`,
    },
    {
      enabled: !!projectId,
      select: (res: any) => res.data?.purchases ?? [],
      defaultData: [],
      ...props,
    },
  );
}

/** Ventes (factures) liées au projet. */
export function useProjectSalesList(projectId: any, props?: any) {
  return useRequestQuery(
    ['PROJECT_SALES', projectId],
    {
      method: 'get',
      url: `projects/${projectId}/transactions/sales`,
    },
    {
      enabled: !!projectId,
      select: (res: any) => res.data?.sales ?? [],
      defaultData: [],
      ...props,
    },
  );
}

/** Écritures de journaux manuels liées au projet. */
export function useProjectJournalsList(projectId: any, props?: any) {
  return useRequestQuery(
    ['PROJECT_JOURNALS', projectId],
    {
      method: 'get',
      url: `projects/${projectId}/transactions/journals`,
    },
    {
      enabled: !!projectId,
      select: (res: any) => res.data?.journals ?? [],
      defaultData: [],
      ...props,
    },
  );
}

export function useRefreshProjects() {
  const queryClient = useQueryClient();

  return {
    refresh: () => {
      queryClient.invalidateQueries(t.PROJECTS);
    },
  };
}
